import React, { Component } from 'react';
import propTypes from 'prop-types';
import Loading from 'react-loading-animation';
import {
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  ButtonGroup,
  Alert
} from 'reactstrap';
import { Redirect } from 'react-router-dom';
import classNames from 'classnames';
import moment from 'moment-timezone';
import InputDatetime from '../../components/Form/InputDatetime/InputDatetime';
import RichTextEditor from '../../components/Form/RichTextEditor';
import SelectVariant, { variantOptionLabel } from '../../components/Form/Select/Async/SelectVariant';
import { ButtonLoading } from '../../components/Button';
import { KICK_POINT_REDEMPTION_PRODUCT_VARIANT_ROUTE_API } from '../../constants';
import {
  errorMessage,
  hasError,
  textValue,
  textChange,
  dateChange,
  dateValue,
  checkChange
} from '../../utils/form';

/**
 * Presentational form for Kick Point Redemption create/detail.
 * Mirrors the Product Variant form baseline (ProductVariant.js) but with only
 * the redemption fields. Description & Terms use the CMS CKEditor wrapper
 * (src/components/Form/RichTextEditor).
 */
export default class KickPointRedemption extends Component {
  constructor(props) {
    super(props);
    this.state = {
      _form: {
        product_variant: null,
        description: '',
        terms: '',
        active: true,
        started_at: null,
        ended_at: null,
      },
      _errorForm: [],
      _errorMsg: null,
      _statusCode: 0,
      _isSubmitProgress: false,
      _redirectToList: false,
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleCheckedChange = this.handleCheckedChange.bind(this);
    this.handleDatePickerChange = this.handleDatePickerChange.bind(this);
    this.handleEditorChange = this.handleEditorChange.bind(this);
    this._handleSelectVariantChange = this._handleSelectVariantChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  componentDidUpdate(prevProps) {
    // After detail loaded (edit mode)
    if (this.props.isLoading === false && prevProps.isLoading !== this.props.isLoading) {
      if (this.props.data.status_code === 200) {
        const d = this.props.data;
        const pv = d.product_variant;
        this.setState({
          _form: {
            ...this.state._form,
            id: d.id,
            product_variant: pv ? { id: pv.id, name: variantOptionLabel(pv) } : null,
            description: d.description || '',
            terms: d.terms || '',
            active: d.active === undefined || d.active === null ? false : d.active,
            started_at: d.started_at || null,
            ended_at: d.ended_at || null,
            created_at: d.created_at || '',
            updated_at: d.updated_at || '',
          }
        });
      }
    }
    // After submit to api
    if (
      this.state._isSubmitProgress &&
      this.props.isSubmitProgress === false &&
      prevProps.isSubmitProgress !== this.props.isSubmitProgress
    ) {
      if (this.props.data.status_code === 200 && this.props.error === null) {
        const isCreate = this.state._form.id === undefined || this.state._form.id === null;
        if (isCreate) {
          // Create lands back on the list, matching every other CMS form here.
          this.setState({ _isSubmitProgress: false, _redirectToList: true });
        } else {
          const { updated_at } = this.props.data;
          this.setState({
            _isSubmitProgress: false,
            _form: { ...this.state._form, updated_at }
          });
        }
      } else {
        this.setState({ _isSubmitProgress: false, _errorForm: { ...this.props.error } });
      }
    }
    if (prevProps.error !== this.props.error && this.props.error) {
      this.setState({ _isSubmitProgress: false, _errorForm: { ...this.props.error } });
    }
  }

  handleChange(e) {
    const _form = textChange(e, this.state._form);
    this.setState({ _form: { ..._form } });
  }

  handleCheckedChange(e) {
    const _form = checkChange(e, this.state._form);
    this.setState({ _form: { ..._form } });
  }

  handleDatePickerChange(value, name) {
    const date = name === 'ended_at' && value ? moment(value).endOf('day') : value;
    const _form = dateChange(date, name, this.state._form);
    this.setState({ _form });
  }

  handleEditorChange(name, data) {
    this.setState(prevState => ({
      _form: { ...prevState._form, [name]: data }
    }));
  }

  _handleSelectVariantChange(option) {
    this.setState({ _form: { ...this.state._form, product_variant: option } });
  }

  _validateForm(form = this.state._form) {
    const { product_variant } = form;
    let errors = {};
    if (!product_variant)
      errors['product_variant_id'] = ['The product variant field is required.'];
    if (Object.keys(errors).length) {
      this.setState({
        _errorForm: { errors: { ...errors } },
        _errorMsg: 'Please fulfill your form.',
        _statusCode: 422
      });
      return true;
    }
    return false;
  }

  onSubmit(e) {
    e.preventDefault();
    if (this._validateForm()) return;
    const { _form } = this.state;
    const payload = {
      product_variant_id: _form.product_variant ? _form.product_variant.id : undefined,
      description: _form.description || null,
      terms: _form.terms || null,
      active: _form.active === undefined || _form.active === null ? false : _form.active,
      started_at: _form.started_at || null,
      ended_at: _form.ended_at || null,
    };
    this.setState({ _isSubmitProgress: true, _errorForm: [] }, () => {
      this.props.onSubmit(payload);
    });
  }

  render() {
    if (this.state._redirectToList) {
      return <Redirect to="/kick_point_redemptions" />;
    }
    const editorKeySuffix = this.state._form.id || 'new';
    return (
      <Loading isLoading={this.props.isLoading}>
        <Col>
          <Alert color="danger" isOpen={this.state._statusCode === 422} toggle={() => this.setState({ _statusCode: 0 })}>
            <strong>Opps...</strong> {this.state._errorMsg}
          </Alert>
          <Form onSubmit={this.onSubmit}>
            <FormGroup
              className={classNames({
                "has-danger has-feedback": hasError("product_variant_id", this.state._errorForm)
              })}
            >
              <Label for="formKprProductVariant">Product Variant</Label>
              <SelectVariant
                id="formKprProductVariant"
                name="product_variant"
                labelWithSku
                endpoint={KICK_POINT_REDEMPTION_PRODUCT_VARIANT_ROUTE_API}
                placeholder="Search by product name or SKU..."
                noResultsText="Cannot find product variant."
                value={this.state._form.product_variant}
                onSelectChange={this._handleSelectVariantChange}
              />
              {errorMessage("product_variant_id", this.state._errorForm)}
            </FormGroup>

            <FormGroup
              className={classNames({
                "has-danger has-feedback": hasError("description", this.state._errorForm)
              })}
            >
              <Label>Description</Label>
              <RichTextEditor
                key={`kpr-description-${editorKeySuffix}`}
                value={textValue("description", this.state._form)}
                handleChange={(data) => this.handleEditorChange("description", data)}
              />
              {errorMessage("description", this.state._errorForm)}
            </FormGroup>

            <FormGroup
              className={classNames({
                "has-danger has-feedback": hasError("terms", this.state._errorForm)
              })}
            >
              <Label>Terms</Label>
              <RichTextEditor
                key={`kpr-terms-${editorKeySuffix}`}
                value={textValue("terms", this.state._form)}
                handleChange={(data) => this.handleEditorChange("terms", data)}
              />
              {errorMessage("terms", this.state._errorForm)}
            </FormGroup>

            <FormGroup
              className={classNames({
                "has-danger has-feedback": hasError("active", this.state._errorForm)
              })}
            >
              <Label for="formKprActive">Set to Active &nbsp;</Label>
              <label className="switch switch-text switch-success-outline-alt">
                <input
                  type="checkbox"
                  className="switch-input"
                  name="active"
                  id="formKprActive"
                  value="true"
                  checked={textValue("active", this.state._form)}
                  onChange={this.handleCheckedChange}
                />
                <span className="switch-label" data-on="Yes" data-off="No"></span>
                <span className="switch-handle"></span>
              </label>
              {errorMessage("active", this.state._errorForm)}
            </FormGroup>

            <Row>
              <Col xs={12} md={6}>
                <FormGroup
                  className={classNames({
                    "has-danger has-feedback": hasError("started_at", this.state._errorForm)
                  })}
                >
                  <Label for="formKprStartedAt">Started At</Label>
                  <InputDatetime
                    dropUp
                    inputProps={{ placeholder: "Pick a Started Date" }}
                    value={dateValue("started_at", this.state._form)}
                    onChange={date => this.handleDatePickerChange(date, "started_at")}
                    timeFormat="HH:mm"
                  />
                  {errorMessage("started_at", this.state._errorForm)}
                </FormGroup>
              </Col>
              <Col xs={12} md={6}>
                <FormGroup
                  className={classNames({
                    "has-danger has-feedback": hasError("ended_at", this.state._errorForm)
                  })}
                >
                  <Label for="formKprEndedAt">Ended At</Label>
                  <InputDatetime
                    dropUp
                    inputProps={{ placeholder: "Pick an Ended Date" }}
                    value={dateValue("ended_at", this.state._form)}
                    onChange={date => this.handleDatePickerChange(date, "ended_at")}
                    timeFormat="HH:mm"
                  />
                  {errorMessage("ended_at", this.state._errorForm)}
                </FormGroup>
              </Col>
            </Row>

            {this.props.edit ? (
              <Row>
                <Col xs={12} md={6}>
                  <FormGroup>
                    <Label>Created At</Label>
                    <Input type="text" readOnly value={textValue("created_at", this.state._form)} />
                  </FormGroup>
                </Col>
                <Col xs={12} md={6}>
                  <FormGroup>
                    <Label>Updated At</Label>
                    <Input type="text" readOnly value={textValue("updated_at", this.state._form)} />
                  </FormGroup>
                </Col>
              </Row>
            ) : null}

            <FormGroup>
              <ButtonGroup>
                <ButtonLoading
                  type="submit"
                  isLoading={this.state._isSubmitProgress}
                  disabled={this.state._isSubmitProgress}
                >
                  Submit
                </ButtonLoading>
              </ButtonGroup>
            </FormGroup>
          </Form>
        </Col>
      </Loading>
    );
  }
}

KickPointRedemption.propTypes = {
  edit: propTypes.bool,
  isLoading: propTypes.bool,
  isSubmitProgress: propTypes.bool,
  data: propTypes.object,
  error: propTypes.object,
  onSubmit: propTypes.func.isRequired,
};

KickPointRedemption.defaultProps = {
  edit: false,
  isLoading: false,
  isSubmitProgress: false,
  data: {},
  error: null,
};
